import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback,
  Spinner
} from 'reactstrap'
import { IPromotion } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import {
  customStylesLabel,
  discountTypeOptions,
  mapSinglePromotionBooth
} from 'lib/util'
import { isEqual } from 'lodash'

interface IProps {
  promotionRecord: IPromotion | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdatePromotion: (arg: any) => Promise<string>
  booths: any[]
  canEdit: boolean
}

const PromotionModal: React.FC<IProps> = ({
  promotionRecord,
  isOpen,
  editMode,
  handleCreateOrUpdatePromotion,
  setOpenPopup,
  booths,
  canEdit
}) => {
  const [promotionBooth, setPromotionBooth] = useState<any>(null)
  const [initPromotionBooths, setInitPromotionBooths] = useState<any>([])
  const [disableSave, setDisableSave] = useState<boolean>(editMode)
  const [fetching, setFetching] = useState<boolean>(false)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const hasEdit = (editMode && promotionRecord) || false

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: hasEdit ? promotionRecord?._id : null,
      name: hasEdit ? promotionRecord?.name : '',
      code: hasEdit ? promotionRecord?.code : '',
      startDateTime: hasEdit && promotionRecord?.startDateTime ? new Date(promotionRecord.startDateTime) : null,
      finishDateTime: hasEdit && promotionRecord?.finishDateTime ? new Date(promotionRecord.finishDateTime) : null,
      discountType: hasEdit ? promotionRecord?.discountType : '',
      discountValue: hasEdit ? promotionRecord?.discountValue : '',
      isActive: hasEdit ? promotionRecord?.isActive : false,
      usageLimit: hasEdit ? promotionRecord?.usageLimit ?? null : null
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please Enter Name'),
      code: Yup.string().required('Please Enter Code'),
      startDateTime: Yup.mixed().required('Please Select Start Date Time'),
      finishDateTime: Yup.mixed()
        .required('Please Select Finish Date Time')
        .test('is-greater', 'Finish time must be later than start time', function (value) {
          const { startDateTime } = this.parent
          if (!value || !startDateTime) return false
          return new Date(value as string | number | Date) > new Date(startDateTime as string | number | Date)
        }),
      discountType: Yup.string().required('Please Select Discount Type'),
      discountValue: Yup.number().required('Please Enter Discount Value')
    }),
    onSubmit: (values) => {
      handleSubmit(values)
    }
  })

  useEffect(() => {
    if (isOpen) {
      formik.resetForm()
      // The logic for setting initPromotionBooths and promotionBooth is moved to a separate useEffect
      // to ensure it runs when promotionRecord or editMode changes, not just when the modal opens.
      // This also ensures formik.resetForm() doesn't interfere with booth initialization.
    } else {
      setPromotionBooth(null)
      setFetching(false) // Reset fetching state when modal closes
    }
  }, [isOpen])

  // New useEffect to handle promotionRecord and editMode changes for booth initialization
  useEffect(() => {
    if (promotionRecord && editMode) {
      setHasChanges(false)

      const parsedBooths = mapSinglePromotionBooth(promotionRecord)

      // Use _id from promotionRecord for MongoDB, fallback to id for SQL Server if needed
      setPromotionBooth({
        id: promotionRecord._id || (promotionRecord as any).id,
        name: promotionRecord.name,
        booths: parsedBooths?.booths || []
      })
      setInitPromotionBooths({
        id: promotionRecord._id || (promotionRecord as any).id,
        name: promotionRecord.name,
        booths: parsedBooths?.booths || []
      })
    } else {
      setHasChanges(false)
      setPromotionBooth({
        name: '',
        booths: []
      })
      setInitPromotionBooths({
        name: '',
        booths: []
      })
    }
  }, [promotionRecord, editMode])

  // Modified useEffect for disableSave logic
  useEffect(() => {
    setDisableSave(
      booths.length > 0 &&
        initPromotionBooths.booths &&
        isEqual(promotionBooth?.booths, initPromotionBooths.booths) &&
        !hasChanges
    )
  }, [hasChanges, promotionBooth, initPromotionBooths, booths])

  useEffect(() => {
    formik.validateForm()
  }, [formik.values.finishDateTime, formik.values.startDateTime])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: any; value: any } }) => {
    const name = e.target.name
    let value =
      name === 'discountValue' && e.target.value ? parseInt(e.target.value) : e.target.value

    // Handle usageLimit as nullable number
    if (name === 'usageLimit') {
      value = e.target.value === '' ? null : parseInt(e.target.value)
    }

    formik.setFieldValue(name, value)
    setHasChanges(true) // Set hasChanges to true on form field change
  }

  const handleSelectAllBooths = () => {
    if (promotionBooth?.booths && promotionBooth.booths.some((b: any) => b.value === 'all')) {
      return booths.map((booth: any) => ({ value: booth.value, label: booth.label }))
    }
    return promotionBooth?.booths || []
  }

  const handleSubmit = async (values: any) => {
    setFetching(true)

    try {
      const boothsParam = handleSelectAllBooths()

      const params = {
        ...values,
        booths: boothsParam.map((booth: any) => booth.value),
        startDateTime: values.startDateTime instanceof Date ? values.startDateTime.toISOString() : values.startDateTime,
        finishDateTime: values.finishDateTime instanceof Date ? values.finishDateTime.toISOString() : values.finishDateTime,
        discountValue: values.discountValue ? Number(values.discountValue) : undefined,
        usageLimit: values.usageLimit ? Number(values.usageLimit) : null
      }
      if (!editMode) {
        delete params.id
      }

      const res = handleCreateOrUpdatePromotion(params)
      const message = await res
      if (message === 'SUCCESSFULL') {
        toast.success(
          editMode ? 'Updated a Promotion successfully' : 'Created a new Promotion successfully'
        )
        setOpenPopup(false)
      } else {
        toast.error(message)
      }
    } catch (e) {
      toast.error('An error occurred')
    } finally {
      setFetching(false)
    }
  }

  const handleChangePromotionActive = (checked: boolean) => {
    formik.setFieldValue('isActive', checked)
    setHasChanges(true)
  }

  const handleGenerateCode = () => {
    // 6 haneli rastgele kod (100000 - 999999)
    const randomCode = Math.floor(100000 + Math.random() * 900000).toString()
    formik.setFieldValue('code', randomCode)
    formik.setFieldTouched('code', true, false)
    setHasChanges(true)
  }

  const handleSelect = (selectValues: any) => {
    setPromotionBooth({ ...promotionBooth, booths: selectValues })
  }

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          togglePopup()
        }}
        scrollable={true}
        id='promotionModal'
      >
        <ModalHeader
          className='modal-title'
          id='promotionModalTitle'
          toggle={() => {
            togglePopup()
          }}
        >
          {editMode ? 'Promotion Record Details' : 'Create a new Promotion Record'}
        </ModalHeader>
        {/* overflow-visible KALDIRILDI: Modal scrollable={true} ile birlikte body'nin
            dikey kaymasını engelliyordu → alttaki Kaydet/Oluştur butonuna erişilemiyordu
            (müşteri talebi 2026-06-26). Booths Select'i menuPortalTarget=document.body
            ile portallandığından dropdown body overflow'undan ETKİLENMEZ; kaldırmak güvenli. */}
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <Col md={12} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                      Name
                    </Label>
                    <Input
                      type='text'
                      maxlength='50'
                      className='form-control'
                      id='name'
                      name='name'
                      placeholder='Name'
                      value={formik.values.name}
                      onChange={(e) => handleOnChange(e)}
                      invalid={formik.touched.name && formik.errors.name ? true : false}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={12} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='code' className='form-label mb-1 fw-semibold'>
                      Code
                    </Label>
                    <div className='d-flex align-items-center gap-2'>
                      <Input
                        type='text'
                        maxlength='20'
                        className='form-control'
                        id='code'
                        name='code'
                        placeholder='Code'
                        value={formik.values.code}
                        onChange={(e) => handleOnChange(e)}
                        invalid={formik.touched.code && formik.errors.code ? true : false}
                      />
                      <Button
                        color='soft-info'
                        size='sm'
                        type='button'
                        className='text-nowrap'
                        onClick={handleGenerateCode}
                      >
                        Oluştur
                      </Button>
                    </div>
                    {formik.touched.code && formik.errors.code ? (
                      <FormFeedback type='invalid' className='d-block'>
                        {formik.errors.code}
                      </FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='discount-type' className='form-label mb-1 fw-semibold'>
                      Discount Type
                    </Label>
                    <select
                      className={`form-select ${formik.touched.discountType && formik.errors.discountType ? 'is-invalid' : ''}`}
                      aria-label='camera-type'
                      name='discountType'
                      value={formik.values.discountType || ''}
                      onChange={(e) => handleOnChange(e)}
                    >
                      {discountTypeOptions.map((dis, index) => {
                        return (
                          <option key={index} value={dis.value}>
                            {dis.label}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.discountType && formik.errors.discountType ? (
                      <FormFeedback type='invalid'>{formik.errors.discountType}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='discount-value' className='form-label mb-1 fw-semibold'>
                      Discount Value
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      max={formik.values.discountType === 'PERCENTAGE' ? 100 : undefined}
                      className='form-control'
                      id='discount-value'
                      placeholder='Discount Value'
                      name='discountValue'
                      onChange={(e) => handleOnChange(e)}
                      value={formik.values.discountValue}
                      invalid={
                        formik.touched.discountValue && formik.errors.discountValue ? true : false
                      }
                    />
                    {formik.touched.discountValue && formik.errors.discountValue ? (
                      <FormFeedback type='invalid'>{formik.errors.discountValue}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='start-date' className='form-label mb-1 fw-semibold'>
                      Start Date Time
                    </Label>
                    <DatePicker
                      selected={formik.values.startDateTime ? formik.values.startDateTime : null}
                      onChange={(date) => {
                        formik.setFieldValue('startDateTime', date ? date : null)
                        setHasChanges(true)
                      }}
                      showTimeInput
                      timeInputLabel='Time:'
                      timeFormat='HH:mm'
                      dateFormat='MM/dd/yyyy h:mm aa'
                      className={`form-control ${
                        formik.touched.startDateTime && formik.errors.startDateTime
                          ? 'is-invalid'
                          : ''
                      }`}
                      name='startDateTime'
                      placeholderText='Select a date'
                      showIcon
                    />
                    {formik.touched.startDateTime && formik.errors.startDateTime ? (
                      <FormFeedback type='invalid' className='d-block'>
                        {formik.errors.startDateTime}
                      </FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div
                    className={`${
                      formik.touched.finishDateTime && formik.errors.finishDateTime
                        ? 'is-invalid'
                        : ''
                    }`}
                  >
                    <Label htmlFor='finishDateTime' className='form-label mb-1 fw-semibold'>
                      Finish Date Time
                    </Label>
                    <DatePicker
                      selected={formik.values.finishDateTime ? formik.values.finishDateTime : null}
                      onChange={(date) => {
                        formik.setFieldValue('finishDateTime', date ? date : null)
                        setHasChanges(true)
                      }}
                      showTimeInput
                      timeInputLabel='Time:'
                      timeFormat='HH:mm'
                      dateFormat='MM/dd/yyyy h:mm aa'
                      className={`form-control ${
                        formik.touched.finishDateTime && formik.errors.finishDateTime
                          ? 'is-invalid'
                          : ''
                      }`}
                      name='finishDateTime'
                      placeholderText='Select a date'
                      showIcon
                    />
                    {formik.touched.finishDateTime && formik.errors.finishDateTime ? (
                      <FormFeedback type='invalid' className='d-block'>
                        {formik.errors.finishDateTime}
                      </FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={12} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='usage-limit' className='form-label mb-1 fw-semibold'>
                      Usage Limit
                      <span className='text-muted ms-2 fw-normal'>(Leave empty for unlimited)</span>
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      className='form-control'
                      id='usage-limit'
                      placeholder='Usage Limit'
                      name='usageLimit'
                      onChange={(e) => handleOnChange(e)}
                      value={formik.values.usageLimit ?? ''}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={12} className='mb-3'>
                <div>
                  <Label htmlFor='booth-value' className='form-label mb-1 fw-semibold'>
                    Booths
                  </Label>
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <Button
                      color='soft-primary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setPromotionBooth({ ...promotionBooth, booths: [...booths] })
                        setHasChanges(true)
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      color='soft-secondary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setPromotionBooth({ ...promotionBooth, booths: [] })
                        setHasChanges(true)
                      }}
                    >
                      Clear All
                    </Button>
                    <span className='text-muted fs-12'>
                      {promotionBooth?.booths?.length || 0} / {booths.length} selected
                    </span>
                  </div>
                  <Select
                    value={promotionBooth?.booths}
                    isMulti={true}
                    isClearable={true}
                    onChange={(selectedMulti2: any) => {
                      handleSelect(selectedMulti2)
                      setHasChanges(true)
                    }}
                    options={booths}
                    styles={customStylesLabel}
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    placeholder='Select booths...'
                  />
                </div>
              </Col>
              <Col md={12} className='mb-3'>
                <div className='form-check'>
                  <Input
                    className='form-check-input'
                    type='checkbox'
                    id='promotion-active'
                    checked={formik.values.isActive}
                    onChange={(e) => handleChangePromotionActive(e.target.checked)}
                  />
                  <Label className='form-check-label fw-medium' htmlFor={`promotion-active`}>
                    Promotion Active
                  </Label>
                </div>
              </Col>
            </Row>
            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Close
              </Button>
              {(canEdit || !editMode) && (
                <>
                  {editMode ? (
                    <Button
                      color='primary'
                      disabled={disableSave || fetching}
                      onClick={() => handleSubmit(formik.values)}
                    >
                      <div className='flex align-items-center'>
                        {fetching ? <Spinner size='sm'> Loading... </Spinner> : 'Save'}
                      </div>
                    </Button>
                  ) : (
                    <Button color='primary' type='submit' disabled={fetching}>
                      <div className='flex align-items-center'>
                        {fetching ? <Spinner size='sm'> Loading... </Spinner> : 'Save'}
                      </div>
                    </Button>
                  )}
                </>
              )}
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}
export default PromotionModal
